import Button from "./Button";
import Spinner from "../spinner/Spinner";
import { createElement, useMemo } from "react";
import { IconType } from "react-icons";
import { MdArrowForward } from "react-icons/md";
import { TranslationKey } from "../../types/modulesInterfaces";
import TranslatableText from "../translations/TranslatableText";
import { useIconSize } from "@app/hooks";

const ModuleButton = ({
	handleClick,
	labelKey,
	buttonType = "submit",
	icon,
	disabled = false,
	isLoading = false,
	outlined = false,
	styleType = "primary",
	customColors,
	translationParams,
	className,
	title,
}: {
	handleClick?: () => void;
	labelKey: TranslationKey;
	buttonType?: "reset" | "button" | "submit";
	icon?: IconType;
	disabled?: boolean;
	isLoading?: boolean;
	outlined?: boolean;
	styleType?: "primary" | "secondary" | "error";
	customColors?: {
		iconBgColor?: string;
		iconColor?: string;
		labelBgColor?: string;
		labelColor?: string;
	};
	translationParams?: { [key: string]: string | number };
	className?: string;
	title?: string;
}) => {
	const styling = useMemo(() => {
		const isSecondary = styleType === "secondary";
		const isError = styleType === "error";
		const defaultColors = {
			iconBgColor: isSecondary ? "bg-tert-de" : isError ? "bg-error" : "bg-quat-br",
			iconColor: isSecondary ? "var(--dark)" : "var(--white)",
			labelBgColor: isSecondary ? "bg-tert-br" : isError ? "bg-error-de" : outlined ? "bg-transp" : "bg-quat-de",
			labelColor: isSecondary ? "var(--dark)" : isError ? "var(--white)" : outlined ? "var(--quaternary-deep)" : "var(--white)",
		};
		return {
			...defaultColors,
			...customColors,
		};
	}, [styleType, outlined, customColors]);

	const buttonClasses = `btn-save w-fit ${outlined ? "outlined-button" : ""} ${disabled ? "op-8" : ""}`;
	const iconSize = useIconSize(20);

	// Use inline styles if custom colors are provided, otherwise use class names
	const useInlineStyles = !!customColors;

	return (
		<Button
			type={buttonType}
			className={`${buttonClasses} ${className}`}
			onClick={handleClick}
			disabled={disabled || isLoading}
			title={title}
		>
			<div 
				className={`${useInlineStyles ? "" : styling.iconBgColor} save-icon`}
				style={useInlineStyles ? { backgroundColor: styling.iconBgColor } : undefined}
			>
				{isLoading ? (
					<Spinner size={36} padding="0.5rem" isBlack={styleType === "secondary"} />
				) : icon ? (
					createElement(icon, { size: iconSize, color: styling.iconColor })
				) : (
					<MdArrowForward size={iconSize} color={styling.iconColor} />
				)}
			</div>
			<span 
				className={`label ff-medium ${useInlineStyles ? "" : styling.labelBgColor}`} 
				style={useInlineStyles 
					? { color: styling.labelColor, backgroundColor: styling.labelBgColor } 
					: { color: styling.labelColor }
				}
			>
				<TranslatableText translationKey={labelKey} translationParams={translationParams} className="lh-1" />
			</span>
		</Button>
	);
};

export default ModuleButton;
